import React, { useState } from "react";
import { Brain, Maximize2, CalendarDays } from "lucide-react";

const card = "rounded-2xl border border-[#00ff90]/20 bg-[#080808] shadow-[0_0_24px_#00ff9010]";
const NEON = "#00ff90";

function AttendanceGauge({ rate = 0 }) {
  const pct   = Math.min(rate, 100);
  const color = pct >= 90 ? NEON : pct >= 75 ? "#fbbf24" : "#f87171";
  const r = 36; const cx = 48; const cy = 48;
  const circ   = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <div className="flex flex-col items-center">
      <svg width={96} height={96} className="-rotate-90">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="text-center -mt-16 mb-10">
        <p className="text-2xl font-bold font-mono" style={{ color }}>{pct.toFixed(1)}%</p>
        <p className="text-xs text-white/30">Today</p>
      </div>
    </div>
  );
}

export default function AttendanceWidget({ data = {}, period, loading, onAskKira, onExpand, expanded }) {
  const [tab, setTab] = useState("overview");

  const depts = data.by_department || [];
  const daily = data.daily_trend   || [];

  return (
    <div className={`${card} flex flex-col`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <div className="flex items-center gap-2">
          <CalendarDays size={14} className="text-[#34d399]/60" />
          <span className="text-sm font-semibold text-white/80">Attendance</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onAskKira(data, "Analyse attendance patterns across departments. Identify chronic absenteeism, trends and recommend interventions.")}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[#34d399]/20 bg-[#34d399]/5 text-[#34d399]/60 hover:text-[#34d399] transition-colors">
            <Brain size={11} /> KIRA
          </button>
          {onExpand && <button onClick={onExpand} className="text-white/20 hover:text-white transition-colors"><Maximize2 size={13} /></button>}
        </div>
      </div>

      <div className="flex gap-1 px-4 pt-3">
        {["overview", "departments", "trend"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors capitalize ${
              tab === t ? "bg-[#34d399]/10 text-[#34d399] border border-[#34d399]/20" : "text-white/30 hover:text-white/60"
            }`}>{t}</button>
        ))}
      </div>

      <div className="p-5 flex-1 space-y-4">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-8 rounded-lg bg-white/5 animate-pulse" />)}</div>
        ) : (
          <>
          {tab === "overview" && (
            <div className="space-y-4">
              <div className="flex items-center gap-6">
                <AttendanceGauge rate={data.today_rate || 0} />
                <div className="flex-1 space-y-3">
                  {[
                    { label: "Monthly Avg",   value: data.monthly_avg ? `${data.monthly_avg}%` : "—",   color: NEON },
                    { label: "WFH Today",     value: data.wfh_today ? `${data.wfh_today}%` : "—",      color: "#60a5fa" },
                    { label: "Late Arrivals", value: data.late_today ?? "—",                            color: "#fbbf24" },
                    { label: "Absent Today",  value: data.absent_today ?? "—",                          color: "#f87171" },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="flex items-center justify-between">
                      <span className="text-xs text-white/30">{label}</span>
                      <span className="text-sm font-mono font-bold" style={{ color }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Day of week pattern */}
              {data.dow_pattern && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-xs text-white/30 uppercase tracking-wider mb-3">Day of Week Pattern</p>
                  <div className="flex gap-2">
                    {["Mon","Tue","Wed","Thu","Fri"].map((day, i) => {
                      const rate = data.dow_pattern?.[day] || 0;
                      const color = rate >= 90 ? NEON : rate >= 75 ? "#fbbf24" : "#f87171";
                      return (
                        <div key={day} className="flex-1 flex flex-col items-center gap-2">
                          <div className="w-full rounded-t-sm" style={{ height: `${rate * 0.6}px`, background: `${color}50`, minHeight: 8 }} />
                          <span className="text-[10px] text-white/30">{day}</span>
                          <span className="text-[10px] font-mono" style={{ color }}>{rate}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "departments" && (
            <div className="space-y-2">
              {depts.length === 0 ? (
                <p className="text-white/20 text-sm italic text-center py-6">No department data.</p>
              ) : depts.sort((a,b) => a.rate - b.rate).map((d, i) => {
                const color = d.rate >= 90 ? NEON : d.rate >= 75 ? "#fbbf24" : "#f87171";
                return (
                  <div key={i} className={`rounded-xl border px-4 py-3 ${d.rate < 75 ? "border-red-500/20 bg-red-500/3" : "border-white/5 bg-white/3"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-white/70">{d.dept}</span>
                      <span className="text-sm font-mono font-bold" style={{ color }}>{d.rate}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5">
                      <div className="h-full rounded-full transition-all" style={{ width: `${d.rate}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === "trend" && (
            <div className="space-y-3">
              {daily.length === 0 ? (
                <p className="text-white/20 text-sm italic text-center py-6">No trend data.</p>
              ) : (
                <div className="space-y-1">
                  {daily.map((d, i) => {
                    const color = d.rate >= 90 ? NEON : d.rate >= 75 ? "#fbbf24" : "#f87171";
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-[10px] text-white/25 w-16 shrink-0">{d.label}</span>
                        <div className="flex-1 h-2 rounded-full bg-white/5">
                          <div className="h-full rounded-full" style={{ width: `${d.rate}%`, background: `${color}60` }} />
                        </div>
                        <span className="text-xs font-mono w-10 text-right" style={{ color }}>{d.rate}%</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}